/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import DeleteIcon from "@Components/StickySidebarViewer/assets/DeleteIcon";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import SlickGrid1, {
  type SlickGrid1Ref,
  type ToolbarItem,
  type ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING } from "@Services/constants";
import { convertDate } from "@Services/luxonHelper";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { referencedFilesInReviews } from "./ReviewsHelper";
import { uploadStringToTemp } from "@Services/tempUpload";
import { PlusIcon } from "@Components/StickySidebarViewer/assets/selectionIcons";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { portalCreateRoot } from "@CubeComponents/html/cellPortals";
import type { ChangeEvent } from "react";
import type { AddReferencedFilesInReviewsRequest } from "@ServerTypes/reviews/reviews.schema";
import type { ModalStateDialogComponentProps } from "@CubeComponents/interface";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * One entry of a review resource's `referencedFiles` array (written by
 * `updateReferencedFilesInReviews`, returned as `addReferencedFilesInReviewsResponse.data`).
 * SERVER GAP: `reviewResourceResultSchema` declares no `referencedFiles` field and the response's
 * `data` is `z.unknown()`, so there is no server type to flow. Shape taken from the service write.
 * A `type` alias (not an interface) so it satisfies the grid's row constraint.
 */
export type ReferencedFileRow = {
  _id?: string;
  awsKey?: string;
  name?: string;
  level?: string | null;
  createdDate?: number;
  userDetails?: { _id?: ObjectIdString; userName?: string; fullName?: string };
};

interface AttachmentsProps {
  readonly closeAttachmentModal: () => void;
  readonly viewMode?: boolean;
  readonly attachments: ReferencedFileRow[];
  /** Sent as `addReferencedFilesInReviewsRequest.fileVersionId`. */
  readonly fileVersionId: NonNullable<AddReferencedFilesInReviewsRequest["fileVersionId"]>;
  /** Sent as `addReferencedFilesInReviewsRequest.reviewId`. */
  readonly reviewId: NonNullable<AddReferencedFilesInReviewsRequest["reviewId"]>;
}

function Attachments({
  closeAttachmentModal,
  viewMode = true,
  attachments,
  fileVersionId,
  reviewId,
}: AttachmentsProps) {
  const { t } = useTranslation();
  const [attachedFilesData, setAttachedFilesData] = useState<ReferencedFileRow[]>(attachments);
  const dataGrid = useRef<SlickGrid1Ref<ReferencedFileRow> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachedFilesDataRef = useRef(attachedFilesData);
  const dispatch = useDispatch();

  const buttonClicked = () => {
    fileInputRef.current!.click();
  };

  const attachBtnTemplate = () => (
    <>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} id="attach" onClick={buttonClicked} className="me-7">
        <span className="flex items-center gap-1">
          <PlusIcon color="var(--primary-text)" />
          {t("Attach")}
        </span>
      </ButtonComponent>
      <input
        type="file"
        ref={fileInputRef}
        accept="image/png,image/jpg,image/jpeg,application/pdf,.xls,.xlsx,.csv,.xml"
        className="hidden"
        onChange={handleFileChange}
      />
    </>
  );

  const removeBtnTemplate = () => (
    <span className="me-7 flex items-center gap-1 cursor-pointer" id="remove">
      <DeleteIcon size="15" className="" />
      {t("Delete")}
    </span>
  );

  const downLoadTemplate = () => (
    <div className="flex items-center cursor-pointer" id="download">
      <IconComponent name="download" width="1.5rem" height="1.5rem" className="me-2" />
      <span>{t("Download")}</span>
    </div>
  );

  const toolbarOptions = useMemo<ToolbarItem[]>(
    () => [
      {
        align: "Left",
        template: attachBtnTemplate,
        id: "attach",
      },
      {
        align: "Left",
        template: removeBtnTemplate,
        id: "remove",
      },
      {
        align: "Left",
        template: downLoadTemplate,
        id: "download",
      },
    ],

    [],
  );

  useEffect(() => {
    attachedFilesDataRef.current = attachedFilesData;
  }, [attachedFilesData]);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    // KNOWN BUG, preserved verbatim: `files` is nullable and `files[0]` may be absent (the change
    // event also fires when a picker is dismissed on some browsers), and `file.name` is read without
    // a guard. Does the server read it? The name is sent as `addReferencedFilesInReviewsRequest.name`
    // and stored, so a missing file would throw here before any request. Fix: early-return when the
    // list is empty — that adds a guard to the emit, so it is left alone.
    const file = e.target.files![0]!;
    const name = file.name;
    const size = file.size;
    fileInputRef.current!.value = "";
    if (size > 10485760) {
      return dispatch(
        showToastNotification({
          content: t("Selected file is larger than {{max, number}} MB!", { max: 10 }),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
    const isDataExist = attachedFilesDataRef.current.find((entry) => entry.name === name);
    if (isDataExist) {
      return dispatch(showToastNotification({ content: t("Selected file already exists!"), type: TOAST_TYPE_WARNING }));
    }

    const tempKey = await uploadStringToTemp(file, name);
    if (!tempKey) {
      return dispatch(showToastNotification({ content: t("File upload failed!"), type: TOAST_TYPE_WARNING }));
    }

    const response = await referencedFilesInReviews({
      uploadRefrencedFiles: tempKey,
      name,
      fileVersionId,
      reviewId,
      action: "update",
    });
    setAttachedFilesData((prev) => [...prev, response.data.data as ReferencedFileRow]);
  };

  const handleDownloadClick = (awsKey?: string, name?: string) => {
    const url = `${getBackendUrl()}/fileSystemServer/referencedFileDownload?awsFileName=${awsKey}&fileName=${name}&origin=${location.hostname}`;
    window.open(url, "_blank");
  };

  const headerContent = () => (
    <div className="  w-full flex justify-between rounded-md items-center">
      <div className="">{viewMode ? t("View Attachments") : t("Add Attachments")}</div>
    </div>
  );

  const toolbarClickHandler = useCallback(
    (toolClicked: string) => {
      const selectedRecords = dataGrid.current?.dataView?.getAllSelectedItems() || [];
      const selectedRecordsKey = selectedRecords.map((entry) => entry._id);

      if (toolClicked === "remove" && selectedRecordsKey.length > 0) {
        const updatedFilesData = attachedFilesDataRef.current.filter(
          (entry) => !selectedRecordsKey.includes(entry._id),
        );
        setAttachedFilesData(updatedFilesData);
        referencedFilesInReviews({
          action: "delete",
          deleteAttachedFiles: selectedRecordsKey as string[],
          reviewId,
          fileVersionId,
        });
      }

      if (toolClicked === "download" && selectedRecordsKey.length === 1) {
        // KNOWN BUG, preserved verbatim: `.find` can return undefined (the selected row may have
        // already been removed from `attachedFilesDataRef` by the branch above in the same click) and
        // the result is destructured without a guard, so Download then throws. Does the server read
        // it? No request is made — it throws client-side before building the download URL. Fix: guard
        // the destructure, which would add emitted code, so it is left alone.
        const { awsKey, name } = attachedFilesDataRef.current.find((entry) => selectedRecordsKey.includes(entry._id))!;
        handleDownloadClick(awsKey, name);
      }
    },
    [reviewId, fileVersionId],
  );

  const rawSelectionEvent = () => {
    if (dataGrid.current) {
      const selectedRecords = dataGrid.current?.dataView?.getAllSelectedItems() || [];
      if (selectedRecords.length === 0) {
        dataGrid.current.toolbarModule?.enableItems(["remove", "download"], false);
      } else if (selectedRecords.length === 1) {
        dataGrid.current.toolbarModule?.enableItems(["remove", "download"], true);
      } else {
        dataGrid.current.toolbarModule?.enableItems(["remove"], true);
        dataGrid.current.toolbarModule?.enableItems(["download"], false);
      }
    }
  };

  const dateFormatter = (
    row: number,
    cell: number,
    value: unknown,
    columnDef: unknown,
    dataContext: ReferencedFileRow,
  ) => {
    const dateFormat = convertDate(Number(dataContext.createdDate), "dateForTooltip");
    const container = document.createElement("div");
    container.style.width = "fit-content";
    portalCreateRoot(container).render(
      <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
        <span>{convertDate(Number(dataContext.createdDate), "dateForText")}</span>
      </ToolTipComponent>,
    );
    return container;
  };

  const userNameFormatter = (
    row: number,
    cell: number,
    value: unknown,
    columnDef: unknown,
    dataContext: ReferencedFileRow,
  ) => {
    const { userDetails } = dataContext;
    const container = document.createElement("div");
    renderUserTag(container, { id: userDetails?._id });
    return container;
  };

  const columnDefinitions = useMemo(
    () => [
      {
        id: "name",
        field: "name",
        name: t("Name"),
        minWidth: 150,
        clipMode: "EllipsisWithTooltip",
      },
      {
        id: "userDetails",
        field: "userDetails.userName",
        name: t("Uploaded by"),
        minWidth: 150,
        formatter: userNameFormatter,
        _valueGetter: (row: ReferencedFileRow) => row?.userDetails?._id,
        _excelValueGetter: (row: ReferencedFileRow) =>
          row?.userDetails?.userName || row?.userDetails?.fullName || row?.userDetails?._id || "",
        disableFiltering: true,
        disableSorting: true,
        disableGrouping: true,
      },
      {
        id: "createdDate",
        field: "createdDate",
        name: t("Uploaded On"),
        minWidth: 150,
        formatter: dateFormatter,
        filterType: "date",
        _excelValueGetter: (row: ReferencedFileRow) =>
          row.createdDate ? convertDate(Number(row.createdDate), "dateForText") : "",
      },
      {
        id: "level",
        field: "level",
        name: t("Uploaded at Level"),
        minWidth: 120,
        clipMode: "EllipsisWithTooltip",
      },
    ],
    [],
  );

  const attachedFilesGrid = useCallback(
    () => (
      <SlickGrid1
        ref={dataGrid}
        toolbar={toolbarOptions}
        dataset={attachedFilesData}
        // Bridged at the prop: `ValidatedColumnDefinition` is branded and only the `typedColumns()`
        // VALUE import can mint it; adopting that import would change the emitted module graph.
        columnDefinitions={
          columnDefinitions as typeof columnDefinitions & ValidatedColumnDefinition<ReferencedFileRow>[]
        }
        toolbarClickHandler={toolbarClickHandler}
        onSelectedRowIdsChanged={rawSelectionEvent}
        rowHeight={50}
        gridHeight="100%"
        enableSortingOption
        infiniteScroll
        enableFilteringOption
        enableGroupingOption
        enableColumnChooser
        excelExport
        hideNoDataIllustration
      />
    ),

    [attachedFilesData, columnDefinitions],
  );

  const modelContent = () => (
    <div className="flex gap-4 flex-col max-h-115 overflow-auto">
      <div className="flex flex-col border-2 border-button_light rounded-t-md h-90">{attachedFilesGrid()}</div>
    </div>
  );

  // KNOWN BUG, preserved verbatim: the spread below sends `closeModal: true`, which is NOT a prop of
  // ModalStateDialogComponent (it reads `onCancel`), so it is silently dropped; `allowModalPadding` IS
  // declared on `ModalStateDialogComponentProps` but ModalWithState never destructures it either, so
  // it is a no-op too. Does the server read it? N/A — client-only. Fix: delete both and rely on
  // `onCancel`/`modalPadding`; kept because removing them changes the emitted props object.
  return (
    <ModalStateDialogComponent
      {...({ allowModalPadding: false, closeModal: true } as ModalStateDialogComponentProps)}
      width="70%"
      headerTitle={headerContent()}
      onCancel={closeAttachmentModal}
      modelContent={modelContent()}
      isVisible
    />
  );
}

export default Attachments;
